import { z } from 'zod';
import { APPOINTMENT_EXPECTED_STATUSES } from '../inquiry/inquiry-list.js';
/**
 * Les **cinq étapes** que la maquette dessine sous chaque lot — le « funnel » de la fiche client,
 * et les colonnes « À planifier / RDV pris / En traitement » du tableau de bord.
 *
 * ⚠️ **Ce n'est pas `Inquiry.status`, et les deux ne se remplacent pas.** M3 porte la position
 * d'**une** demande dans son cycle de vie ; ceci est le regroupement d'**affichage** qu'une
 * campagne en fait, exactement comme `ClientFamily` regroupe les cinq catégories de client. Sept
 * segments sur une barre de 180 pixels ne se lisent pas, et deux d'entre eux — `archived`,
 * `dropped` — ne disent rien d'un avancement.
 *
 * ⚠️ **Les identifiants sont en anglais** là où la maquette abrège en français
 * (`recu/plan/rdv/trait/valid`) : ce sont des valeurs d'énumération d'un contrat d'API. Les
 * libellés imprimés, eux, restent ceux du métier (`LOT_PIPELINE_STAGE_LABELS`).
 *
 * L'ordre est celui de la barre, de la demande qui vient d'arriver au contrôle validé.
 */
export const LOT_PIPELINE_STAGES = [
    'received',
    'to_plan',
    'scheduled',
    'processing',
    'validated',
];
export const lotPipelineStageSchema = z.enum(LOT_PIPELINE_STAGES);
/**
 * Ce que chaque étape imprime — sur la légende de la barre comme sur la pastille du contrôle
 * unique d'un particulier.
 *
 * Des **libellés, pas des identifiants** : en français par la troisième exception du glossaire,
 * et ce sont les mots de la maquette, terme à terme.
 */
export const LOT_PIPELINE_STAGE_LABELS = {
    received: 'Reçue',
    to_plan: 'À planifier',
    scheduled: 'RDV planifié',
    processing: 'Traité',
    validated: 'Validé',
};
/**
 * La partition, écrite **une seule fois** : c'est elle que lit le `count(*) FILTER (WHERE …)` du
 * dépôt, et elle encore que lit `lotPipelineStageOf`. Deux écritures séparées finiraient par
 * ranger une demande affectée d'un côté pour le compteur et de l'autre pour la pastille — la
 * barre annoncerait alors un effectif que la fiche ne montre pas.
 *
 * ⚠️ **Deux signaux, pas un.** `to_plan` et `scheduled` ne se départagent pas au statut : une
 * demande « planifiée » n'est que le fait qu'une ligne existe dans `appointment` (M14, M24), et
 * c'est déjà le prédicat des onglets « À planifier » / « Avec RDV » du tableau des demandes. La
 * trancher au statut en donnerait un second, incompatible — `assigned` avec un créneau posé est
 * le cas **normal** de « RDV pris », et le compter en « À planifier » viderait le segment bleu de
 * la barre.
 *
 * ⚠️ **`dropped` n'a pas d'étape, et c'est la décision plutôt qu'un trou.** Une demande
 * abandonnée n'avance plus : la ranger dans un segment la ferait lire comme du retard, et la
 * ranger dans `validated` comme un contrôle fait. Elle sort de la barre, comme le tableau de bord
 * la sort déjà de « Contrôles en cours ». Conséquence assumée : la somme des cinq étapes vaut le
 * nombre de demandes **non abandonnées** du lot, pas son total.
 *
 * ⚠️ `archived` est compté avec `validated` : c'est un contrôle validé qu'on a rangé, et la
 * maquette n'a pas de sixième segment pour le dire.
 */
export const LOT_PIPELINE_PARTITION = {
    received: { statuses: ['received'], appointment: 'unscheduled' },
    to_plan: { statuses: ['assigned', 'in_progress'], appointment: 'unscheduled' },
    // Les trois statuts où un rendez-vous a un sens (`APPOINTMENT_EXPECTED_STATUSES`) : au-delà, le
    // contrôle est transmis et ce n'est plus le créneau qui dit où il en est.
    scheduled: { statuses: APPOINTMENT_EXPECTED_STATUSES, appointment: 'scheduled' },
    processing: { statuses: ['submitted'], appointment: 'any' },
    validated: { statuses: ['validated', 'archived'], appointment: 'any' },
};
/**
 * L'étape d'une demande, **lue dans la table ci-dessus** — donc la même source que le compteur.
 *
 * `null` pour une demande abandonnée, seul cas sans étape : l'appelant décide s'il l'ignore ou
 * s'il en parle, ce qui n'est pas la même réponse sur une barre et sur une pastille.
 */
export function lotPipelineStageOf(status, scheduled) {
    const matched = LOT_PIPELINE_STAGES.find((stage) => {
        const rule = LOT_PIPELINE_PARTITION[stage];
        if (!rule.statuses.includes(status))
            return false;
        return rule.appointment === 'any' || (rule.appointment === 'scheduled') === scheduled;
    });
    return matched ?? null;
}
/**
 * Le funnel d'un lot : combien de demandes à chaque étape, **compté en base** (D24) et jamais en
 * additionnant une page côté navigateur.
 *
 * ⚠️ Les cinq étapes sont exigées : `z.record` sur l'énumération réclame toutes ses clés, si bien
 * qu'une étape absente de la réponse ne peut pas se lire « aucune demande » sur la barre alors
 * qu'elle veut dire « le serveur ne l'a pas comptée ».
 */
export const lotPipelineSchema = z.record(lotPipelineStageSchema, z.number().int().min(0));
/** Un funnel vide — le lot qu'on vient d'ouvrir, et le point de départ d'un cumul. */
export const EMPTY_LOT_PIPELINE = {
    received: 0,
    to_plan: 0,
    scheduled: 0,
    processing: 0,
    validated: 0,
};
/**
 * Le dénominateur du « X / Y validés » de la maquette : les demandes **que la barre montre**.
 *
 * ⚠️ Ce n'est donc pas le nombre de demandes du lot — les abandonnées n'ont pas d'étape (cf.
 * `LOT_PIPELINE_PARTITION`). Écrit ici plutôt que dans chaque écran : trois additions recopiées
 * dérivent au premier segment ajouté.
 */
export function lotPipelineTotal(pipeline) {
    return LOT_PIPELINE_STAGES.reduce((sum, stage) => sum + pipeline[stage], 0);
}
//# sourceMappingURL=lot-pipeline.js.map